package com.narbase.letsgo.web.views.voiceassistant

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.lifecycle.LifecycleOwner
import com.narbase.letsgo.dto.KmmLong
import com.narbase.letsgo.dto.enum
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.dto.voiceassistant.VoiceAssistantSessionLogDto
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantConfidence
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantEngine
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantField
import com.narbase.letsgo.web.models.voiceassistant.VoiceAssistantOutcome
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.scrollable.scrollable
import com.narbase.letsgo.web.utils.table.headerCell
import com.narbase.letsgo.web.utils.table.listTable
import com.narbase.letsgo.web.utils.table.tableCell
import com.narbase.letsgo.web.utils.table.tableRow
import com.narbase.letsgo.web.utils.verticalSeparator
import com.narbase.letsgo.web.utils.views.*
import kotlin.js.Date

class VoiceAssistantSessionsComponent : Component() {

    private val viewModel = VoiceAssistantSessionsViewModel()
    private val sessionDetailsDialog = VoiceAssistantSessionDetailsDialog(viewModel)

    private var sessionsTableBody: View? = null
    private var paginationControls: PaginationControls? = null

    override fun onViewMounted(lifecycleOwner: LifecycleOwner) {
        super.onViewMounted(lifecycleOwner)
        viewModel.getSessions()
    }

    override fun View?.getView() = view {
        id = "voiceAssistantSessionsRootView"
        style { matchParentDimensions }
        scrollable {
            style { matchParentDimensions }
            verticalLayout {
                style {
                    width = matchParent
                    height = wrapContent
                    padding = 32.px
                }
                pageTitle()
                sessionsSection()
            }
        }
    }

    private fun LinearLayout.pageTitle() {
        textView {
            text = "Voice Assistant Sessions".localized()
            style {
                width = wrapContent
                fontSize = 20.px
                fontWeight = "bold"
            }
        }
    }

    private fun LinearLayout.sessionsSection() {
        verticalLayout {
            style {
                width = matchParent
                marginTop = 16.px
            }

            horizontalLayout {
                style { width = matchParent }
                horizontalFiller()
                searchTextInput("Search".localized()) { viewModel.searchSessionsFor(it) }
                refreshSessionsButton()
            }

            verticalLayout {
                style {
                    width = matchParent
                    marginTop = 12.px
                }
                withLoadingAndError(
                    uiState = viewModel.sessionsUiState,
                    onRetryClicked = { viewModel.getSessions() },
                    onLoaded = { onSessionsLoaded() },
                )
                sessionsTableBody = listTable {
                    headerCell("Passenger".localized(), 2)
                    headerCell("Turns".localized(), 1)
                    headerCell("Transcript".localized(), 4)
                    headerCell("Engine".localized(), 1)
                    headerCell("Chat model".localized(), 2)
                    headerCell("Transcription model".localized(), 2)
                    headerCell("Searches".localized(), 1)
                    headerCell("Audio".localized(), 1)
                    headerCell("Confidence".localized(), 1)
                    headerCell("Outcome".localized(), 2)
                    headerCell("Ride".localized(), 2)
                    headerCell("Took".localized(), 1)
                    headerCell("Time".localized(), 2)
                }
                paginationControls = setupPaginationControls(viewModel::getNextPage, viewModel::getPreviousPage)
            }
        }
    }

    private fun LinearLayout.refreshSessionsButton() {
        materialIcon("refresh") {
            style {
                marginStart = 8.px
                alignSelf = Alignment.Center
                color = AppColors.mainColor
                pointerCursor()
            }
            onClick = { viewModel.getSessions() }
        }
    }

    private fun onSessionsLoaded() {
        paginationControls?.update(viewModel.pageNo, viewModel.pageSize, viewModel.total)
        sessionsTableBody?.clearAllChildren()
        sessionsTableBody?.apply {
            viewModel.sessions.forEachIndexed { index, session ->
                tableRow {
                    style {
                        alignItems = Alignment.Center
                        paddingEnd = 8.px
                        pointerCursor()
                    }
                    tableCell(session.passengerName ?: session.passengerId.toString(), 2, 14.px)
                    tableCell(session.turns.size.toString(), 1, 14.px)
                    tableCell(session.joinedTranscript(), 4, 14.px)
                    tableCell(session.engine.enum<VoiceAssistantEngine>().name, 1, 14.px)
                    tableCell(session.lastTurn()?.chatModel ?: "N/A", 2, 14.px)
                    tableCell(session.lastTurn()?.transcriptionModel ?: "N/A", 2, 14.px)
                    tableCell(session.turns.sumOf { it.searches.size }.toString(), 1, 14.px)
                    tableCell(if (session.turns.any { it.hasAudio }) "Yes".localized() else "No".localized(), 1, 14.px)
                    tableCell(session.confidence.enum<VoiceAssistantConfidence>().name, 1, 14.px)
                    tableCell(session.outcome.enum<VoiceAssistantOutcome>().name, 2, 14.px)
                    tableCell(session.rideSummary(), 2, 14.px)
                    tableCell(session.totalDurationMillis.asReadableDuration(), 1, 14.px)
                    tableCell(formatTimestamp(session.createdAt), 2, 14.px)
                    onClick = { sessionDetailsDialog.show(session) }
                }
                if (index != viewModel.sessions.lastIndex) verticalSeparator()
            }
        }
    }

    private fun VoiceAssistantSessionLogDto.lastTurn() = turns.lastOrNull()

    private fun VoiceAssistantSessionLogDto.rideSummary(): String {
        val id = rideId ?: return "Not created".localized()
        return "#${id.toLong()} ${rideStatus ?: "?"}"
    }

    private fun VoiceAssistantSessionLogDto.joinedTranscript() = turns
        .filter { it.transcript.isNotBlank() }
        .joinToString(" | ") { "${it.field.enum<VoiceAssistantField>().name}: ${it.transcript}" }
        .ifBlank { "N/A" }

    private fun formatTimestamp(epochMillis: KmmLong): String =
        Date(epochMillis).toLocaleString()
}
